import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { formatApiErrorDetail } from "../lib/api";

export default function AdminLoginPage() {
  const { login, user, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [wrongSurface, setWrongSurface] = useState(false);

  useEffect(() => {
    if (!loading && user) {
      // If a valid chaplain session already exists (via /chaplain-portal/login),
      // send them back to the chaplain portal instead of showing this page.
      if (user.role === "chaplain") {
        navigate("/chaplain-portal/dashboard", { replace: true });
        return;
      }
      const dest = user.must_change_password ? "/care-portal/admin/change-password" : "/care-portal/admin/dashboard";
      navigate(dest, { replace: true });
    }
  }, [user, loading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setWrongSurface(false);
    try {
      const authed = await login(email, password, "management_center");
      toast.success("Welcome back");
      navigate(authed?.must_change_password ? "/care-portal/admin/change-password" : "/care-portal/admin/dashboard");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "wrong_surface") {
        setWrongSurface(true);
      } else {
        toast.error(formatApiErrorDetail(detail) || err.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-180px)] flex items-center justify-center px-4 py-12" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img
            src="/ccchaps-logo.png"
            alt="California Corporate Chaplains"
            className="h-16 w-16 object-contain mx-auto mb-5"
          />
          <h1 className="font-heading text-3xl tracking-tight text-slate-900">Management Center Login</h1>
          <p className="mt-2 text-sm text-slate-500">Owner and Admin access to the CCCHAPS Management Center.</p>
        </div>

        {wrongSurface && (
          <div
            data-testid="admin-login-wrong-surface"
            className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
          >
            <p className="font-medium">This account uses the CCCHAPS Chaplain Portal.</p>
            <p className="mt-1">Please sign in through Chaplain Login.</p>
            <Link
              to="/chaplain-portal/login"
              className="inline-flex items-center mt-3 px-3 py-1.5 rounded-lg bg-amber-600 text-white font-medium hover:bg-amber-700"
              data-testid="admin-login-go-chaplain"
            >
              Go to Chaplain Login →
            </Link>
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 shadow-sm space-y-5" data-testid="admin-login-form">
          <label className="block">
            <span className="block text-sm font-medium text-slate-700 mb-1.5">Email</span>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-login-email"
              className="w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A6FA5] focus:border-transparent"
            />
          </label>
          <label className="block">
            <span className="block text-sm font-medium text-slate-700 mb-1.5">Password</span>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-login-password"
              className="w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A6FA5] focus:border-transparent"
            />
          </label>
          <button
            type="submit"
            disabled={submitting}
            data-testid="admin-login-submit"
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B] transition-colors disabled:opacity-60"
          >
            {submitting ? "Signing in..." : "Sign in"}
          </button>
          <div className="text-center">
            <Link
              to="/care-portal/admin/forgot-password"
              data-testid="admin-login-forgot-password"
              className="text-xs text-slate-500 hover:text-[#4A6FA5] underline decoration-slate-300 underline-offset-2"
            >
              Forgot password?
            </Link>
          </div>
          <p className="text-xs text-slate-500 flex items-center gap-2 justify-center">
            <ShieldCheck className="w-3.5 h-3.5 text-[#4A6FA5]" /> Sessions expire after 24 hours.
          </p>
          <p className="text-xs text-slate-500 text-center pt-2 border-t border-slate-100">
            Chaplain? <Link to="/chaplain-portal/login" className="text-[#4A6FA5] hover:underline">Sign in at Chaplain Login →</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
